import axios from 'axios';

import { ConversationDto } from '@tallo/investor/conversation';
import { TimeFilter } from '@tallo/utility';

import { PropertyInquiryDto } from '../model/property-inquiry.interface';

class PropertyInquiriesService {
  async getAllInquiriesByRenter(renterId: string): Promise<PropertyInquiryDto[]> {
    const response = await axios.get<PropertyInquiryDto[]>(`property-inquiry/inquiries/renter/${renterId}`);

    return response.data;
  }

  async getInquiryConversation(inquiryId: string): Promise<ConversationDto> {
    const response = await axios.get<ConversationDto>(`property-inquiry/conversation/${inquiryId}`);

    return response.data;
  }

  async getAllInquiriesByProperty(propertyId: string, filter?: TimeFilter): Promise<PropertyInquiryDto[]> {
    const response = await axios.get<PropertyInquiryDto[]>(`property-inquiry/inquiries/property/${propertyId}`, {
      params: {
        filter,
      },
    });

    return response.data;
  }

  async getWantsToApplyByProperty(propertyId: string): Promise<PropertyInquiryDto[]> {
    const response = await axios.get<PropertyInquiryDto[]>(`property-inquiry/wants-to-apply/property/${propertyId}`);

    return response.data;
  }

  async getWantsToApplyByCompany(companyId: string): Promise<PropertyInquiryDto[]> {
    const response = await axios.get<PropertyInquiryDto[]>(`property-inquiry/wants-to-apply/company/${companyId}`);

    return response.data;
  }

  /** Takes the renter off the unit's Wants to apply lists for good, even if they ask again. Nothing is sent to them. */
  async ignoreWantsToApply(inquiryId: string): Promise<void> {
    await axios.put(`property-inquiry/wants-to-apply/${inquiryId}/ignore`);
  }

  /**
   * Tells a renter how to apply where the home takes applications outside Tallo, as a note from the owner, then takes
   * them off the lists. Refused (406) with the screen's reason when the words can't go out as written.
   */
  async answerWantsToApply(inquiryId: string, message: string): Promise<AnswerWantsToApplyResultDto> {
    const response = await axios.post<AnswerWantsToApplyResultDto>(
      `property-inquiry/wants-to-apply/${inquiryId}/answer`,
      { message },
    );

    return response.data;
  }

  /**
   * Asks a renter who wants to apply without touring to tour first, as a note from the owner, then takes them off the
   * lists until they ask again. Refused (409) when the ask is not one without a tour or was already answered.
   */
  async askToTourFirst(inquiryId: string): Promise<AnswerWantsToApplyResultDto> {
    const response = await axios.post<AnswerWantsToApplyResultDto>(
      `property-inquiry/wants-to-apply/${inquiryId}/tour-first`,
    );

    return response.data;
  }
}

export interface AnswerWantsToApplyResultDto {
  /** The note exactly as the renter received it */
  deliveredMessage: string;
}

export default new PropertyInquiriesService();
